import styled from 'styled-components';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { FullWidth } from 'ui/utility/fullWidth';
import { DoubleArrow } from 'ui/icons/navigation/DoubleArrow';
import { UpArrow } from 'ui/icons/navigation/UpArrow';
import { DownArrow } from 'ui/icons/navigation/DownArrow';
import { HeaderGridRow } from 'ui/indexPages/tables/grid';
import { colors, spacing } from 'ui/theme';
import { button } from 'ui/theme/typography';
import { pxToRem } from 'utils/convert';
import { useSortingContext } from 'utils/contexts/sorting';

import { TableRow } from './TableRow';
import { CREATIVE_LIBRARY_COLUMNS, TABLE_GRID } from '../constants';
import { GroupSelect } from '../common/GroupSelect';
import { useAssetsQueryContext } from '../../../contexts/AssetsQueryContext';
import { TableSkeleton } from './TableSkeleton';
import { TableFolderRow } from './TableFolderRow';
import { EmptyStateContent } from '../common/EmptyStateContent';
import { useViewContext } from 'modules/creativeLibrary/contexts/ViewContext';

const TableWrapper = styled(FullWidth)`
  min-width: ${pxToRem(1100)};
`;

type HeaderButtonProps = {
  isActiveSortColumn: boolean;
};

const HeaderButton = styled.button`
  ${button}
  display: flex;
  align-items: center;
  background: none;
  border: none;
  padding: ${spacing.half} 0;
  color: ${(props: HeaderButtonProps) =>
    props.isActiveSortColumn ? colors.purple100 : colors.textPrimary};
  cursor: pointer;
  outline: none;
  text-align: left;

  span {
    display: inline-block;
    margin-right: ${spacing.quarter};
  }
`;

type TableHeaderButtonProps = {
  /** Unique id for this column */
  id: string;
  /** True if column is active sort column  */
  isActiveSortColumn: boolean;
  /** Label to display in column header */
  label: string;
  /** Function run when sortBy value is changed */
  onSortByChange: (newSortedById: string) => void;
  /** Function run when sort direction is changed */
  onSortDirectionChange: (sortDirection: SORT_DIRECTIONS) => void;
  /** Current sort direction */
  sortDirection: SORT_DIRECTIONS;
  /** True if column is sortable */
  isSortable?: boolean;
};

const TableHeaderButton = ({
  id,
  isActiveSortColumn = false,
  isSortable = true,
  label,
  onSortByChange,
  onSortDirectionChange,
  sortDirection,
}: TableHeaderButtonProps) => {
  const { ASCENDING, DESCENDING } = SORT_DIRECTIONS;
  const isAscending = sortDirection === ASCENDING;
  return (
    <HeaderButton
      data-test={`column-header-${id}`}
      isActiveSortColumn={isActiveSortColumn}
      key={id}
      onClick={() => {
        if (isActiveSortColumn) {
          onSortDirectionChange(isAscending ? DESCENDING : ASCENDING);
        } else {
          onSortByChange(id);
          onSortDirectionChange(DESCENDING);
        }
      }}
    >
      <span>{label}</span>
      {!isActiveSortColumn && isSortable && <DoubleArrow isColorInherited={true} />}
      {isActiveSortColumn && isAscending && <UpArrow isColorInherited={true} />}
      {isActiveSortColumn && !isAscending && <DownArrow isColorInherited={true} />}
    </HeaderButton>
  );
};

const TableContent = () => {
  const { assets, folders, isLoading, isPanel } = useAssetsQueryContext();
  const { hasManageBaseTemplatesPermission } = useViewContext();

  if (isLoading) {
    return <TableSkeleton isPanel={isPanel} />;
  }

  return (
    <>
      <EmptyStateContent />
      {(folders.length > 0 || assets.length > 0) && (
        <>
          {folders.map((folder) => (
            <TableFolderRow
              key={folder.id}
              folder={folder}
              isPanel={isPanel}
              hasManageBaseTemplatesPermission={hasManageBaseTemplatesPermission}
            />
          ))}
          {assets.map((asset) => (
            <TableRow
              key={asset.id}
              asset={asset}
              isPanel={isPanel}
              hasManageBaseTemplatesPermission={hasManageBaseTemplatesPermission}
            />
          ))}
        </>
      )}
    </>
  );
};

export const Table = () => {
  const { sortBy, sortDirection, setSortBy, setSortDirection } = useSortingContext();
  const { assets, folders, isPanel } = useAssetsQueryContext();
  const { hasManageBaseTemplatesPermission } = useViewContext();

  return (
    <TableWrapper>
      <HeaderGridRow gridLayout={TABLE_GRID} data-test="creative-library-table-header">
        {!hasManageBaseTemplatesPermission || isPanel ? (
          <div />
        ) : (
          <GroupSelect assets={assets} folders={folders} />
        )}
        {CREATIVE_LIBRARY_COLUMNS.map((column) => (
          <TableHeaderButton
            key={column.id}
            id={column.id}
            label={column.label}
            sortDirection={sortDirection}
            onSortByChange={setSortBy}
            onSortDirectionChange={setSortDirection}
            isActiveSortColumn={column.id === sortBy}
          />
        ))}
      </HeaderGridRow>
      <TableContent />
    </TableWrapper>
  );
};
